import { CardGridLayout, GridInput } from '@column/lib/components/Card/Grid';
import { ColumnSelect } from '@column/lib/components/ColumnSelect';
import { useState } from 'react';
import {
  FilingType,
  isOrderFilingType,
  DynamicSection
} from '@column/lib/types/filingType';
import { TextField } from '@column/lib/components/TextField';
import { SELECTABLE_LAYOUTS } from '@column/lib/orders/layouts';
import CheckboxGroup from '@column/lib/components/Checkbox/CheckboxGroup';
import { SwitchControlledCards } from '@column/lib/components/Card/SwitchControlledCards';
import { LayoutModel } from '@column/lib/model/objects/layoutModel';
import LabeledSwitch from 'lib/components/LabeledSwitch';
import { useAppSelector } from 'redux/hooks';
import { selectActiveOrganization } from 'redux/auth';
import { MADLIB_ID_PATTERN } from '../noticeTypesHelpers';
import FilingTypeModularSizesSettings from './FilingTypeModularSizesSettings';
import DynamicSectionElement from './DynamicSectionElement';

type OrderEntryMethod = 'standard' | 'madlib';

type SuperUserOrderFilingTypeSettingsProps<T extends FilingType> = {
  filingType: T;
  updatedFilingType: T;
  onUpdateFilingType: (filingType: T) => void;
};

export default function SuperUserOrderFilingTypeSettings<T extends FilingType>({
  filingType,
  updatedFilingType,
  onUpdateFilingType
}: SuperUserOrderFilingTypeSettingsProps<T>) {
  const activeOrganization = useAppSelector(selectActiveOrganization);

  const [entryMethod, setEntryMethod] = useState<OrderEntryMethod>(
    updatedFilingType.madlib ? 'madlib' : 'standard'
  );

  if (!isOrderFilingType(updatedFilingType)) {
    return <></>;
  }

  if (!activeOrganization) {
    return <></>;
  }

  const { supportedLayoutIds = [] } = updatedFilingType;
  return (
    <>
      <CardGridLayout
        header={{
          title: 'Category Format',
          description:
            'Configure special formatting settings for this category.'
        }}
      >
        <GridInput fullWidth={entryMethod === 'standard'}>
          <ColumnSelect<OrderEntryMethod>
            id="notice-type-entry-method"
            labelText="Content creation method"
            options={[
              {
                value: 'standard',
                label: 'Enter text and upload images using default editor'
              },
              {
                value: 'madlib',
                label: 'Complete fill-in-the-blank template (Madlib)'
              }
            ]}
            value={entryMethod}
            onChange={newValue => {
              const newFilingType = { ...updatedFilingType };
              if (newValue === 'standard') {
                newFilingType.madlib = '';
                setEntryMethod('standard');
              } else if (newValue === 'madlib') {
                newFilingType.madlib = filingType.madlib;
                setEntryMethod('madlib');
              }
              onUpdateFilingType(newFilingType);
            }}
          />
        </GridInput>
        {entryMethod === 'madlib' && (
          <GridInput>
            <TextField
              id="notice-type-madlib-id"
              value={updatedFilingType.madlib || ''}
              labelText="Madlib storage path"
              pattern={MADLIB_ID_PATTERN}
              validationMessages={{
                patternMismatch: 'Please input a valid madlib config.',
                valueMissing: 'Please enter a path to a madlib config.'
              }}
              required
              onChange={newValue => {
                onUpdateFilingType({
                  ...updatedFilingType,
                  madlib: newValue
                });
              }}
              placeholder="madlibs/publisher/folder"
            />
          </GridInput>
        )}
        <GridInput fullWidth>
          <LabeledSwitch
            label="Enable default category extra fields"
            description="Certain default categories show extra required fields during placement if enabled (ex: locations for real estate ads)."
            onChange={value =>
              onUpdateFilingType({
                ...updatedFilingType,
                disableCategoryFields: !value
              })
            }
            value={!updatedFilingType.disableCategoryFields}
          />
        </GridInput>
        <SwitchControlledCards
          labelProps={{
            value: !!updatedFilingType.dynamicSections?.length,
            onChange: newValue => {
              if (!newValue) {
                onUpdateFilingType({
                  ...updatedFilingType,
                  dynamicSections: null
                });
                return;
              }

              // Create a default header section when enabling
              const defaultSection: DynamicSection = {
                id: 'header',
                name: 'Header',
                template: '',
                textStyle: '',
                objectStyle: ''
              };

              onUpdateFilingType({
                ...updatedFilingType,
                dynamicSections: [defaultSection]
              });
            },
            label: 'Enable dynamic sections',
            description:
              'Configure multiple content sections with individual templates and styling.'
          }}
          cardProps={{
            Component: DynamicSectionElement,
            baseHeader: 'Dynamic Section',
            onChange: dynamicSections =>
              onUpdateFilingType({
                ...updatedFilingType,
                dynamicSections:
                  dynamicSections.length > 0 ? dynamicSections : undefined
              }),
            values: updatedFilingType.dynamicSections ?? [],
            archivable: {
              confirmation: {
                header: 'Remove dynamic section?',
                body: 'Are you sure you want to remove this dynamic section?'
              },
              buttonText: 'Remove section',
              shouldAllow: () => true
            },
            createable: {
              buttonText: 'Add another section',
              initialValue: {
                id: '',
                name: '',
                template: ''
              }
            }
          }}
        />
      </CardGridLayout>
      <CardGridLayout
        header={{
          title: 'Filing Type Layouts',
          description: 'Set up layouts'
        }}
      >
        <GridInput fullWidth>
          <CheckboxGroup
            id="supported-layouts"
            labelText="Supported layouts"
            options={SELECTABLE_LAYOUTS.map(
              layout => new LayoutModel(layout)
            ).map(layoutModel => ({
              id: layoutModel.layout.id,
              labelText: layoutModel.description,
              labelDescription: `id: ${layoutModel.layout.id}, display: ${layoutModel.isDisplayLayout}, split columns: ${layoutModel.isSplitColumns}`,
              checked: supportedLayoutIds.includes(layoutModel.layout.id),
              value: layoutModel.layout.id
            }))}
            type="checkbox"
            onChange={newLayouts => {
              const selectedLayouts = newLayouts.filter(
                layout => layout.checked
              );
              onUpdateFilingType({
                ...updatedFilingType,
                supportedLayoutIds: selectedLayouts.map(layout => layout.id)
              });
            }}
          />
        </GridInput>
      </CardGridLayout>
      <FilingTypeModularSizesSettings
        updatedFilingType={updatedFilingType}
        onUpdateFilingType={onUpdateFilingType}
      />
    </>
  );
}
